import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { Button } from '../../../shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../shared/ui/card';
import { Input } from '../../../shared/ui/input';
import { Label } from '../../../shared/ui/label';

const INITIAL_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export default function ChangePasswordPage() {
  const { changePassword, isAuthenticated, mustChangePassword } = useSession();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  function updateField(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function validateForm() {
    if (!form.currentPassword.trim()) {
      return 'Inserisci la password attuale.';
    }

    if (!form.newPassword.trim()) {
      return 'Inserisci la nuova password.';
    }

    if (form.newPassword !== form.confirmPassword) {
      return 'Le password non coincidono.';
    }

    return '';
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setForm(INITIAL_FORM);
      navigate('/', { replace: true });
    } catch (changeError) {
      setError(changeError.message || 'Cambio password non riuscito.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--surface-raised)] px-6 py-12">
      <div className="mx-auto max-w-xl">
        <Card>
          <CardHeader>
            <CardTitle>Cambia password</CardTitle>
            <CardDescription>
              {mustChangePassword
                ? 'La password iniziale o resettata e temporanea. Sostituiscila per continuare.'
                : 'Aggiorna la password del tuo account.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="current-password">Password attuale</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={form.currentPassword}
                  disabled={isSubmitting}
                  autoComplete="current-password"
                  onChange={(event) => updateField('currentPassword', event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Nuova password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={form.newPassword}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  onChange={(event) => updateField('newPassword', event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Conferma password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={form.confirmPassword}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  onChange={(event) => updateField('confirmPassword', event.target.value)}
                />
              </div>

              {error ? (
                <div role="alert" className="text-sm wt-inline-error">
                  {error}
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Salvataggio...' : 'Salva nuova password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
